import React, { useState } from "react";
import MatchTimer from "./MatchTimer";
import { useTheme } from "../config/ThemeContext";
import ConfirmationDialog from "./ConfirmationDialog"; 
import useNetworkStatus from "../hooks/useNetworkStatus";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

function MatchHeader({ teamAName, teamBName, currentSet, matchId, socket }) {
  const colors = useTheme();
  const isOnline = useNetworkStatus(); 
  const [showEndSetDialog, setShowEndSetDialog] = useState(false);

  const handleViewReportP2 = () => {
    window.open(`/matches/${matchId}/report`, "_blank");
  };

  const handleViewReportP3 = () => {
    window.open(`/matches/${matchId}/players-ranking`, "_blank");
  };

  const handleEndSetConfirm = () => {
    socket.emit("setHasEnded", { matchId });
    setShowEndSetDialog(false);
  };

  return (
    <>
      {/* Offline Notification */}
      {!isOnline && (
        <div className="
          bg-gradient-to-r from-red-500 to-red-600
          text-white text-center py-2 px-4
          animate-pulse
        ">
          <div className="flex items-center justify-center gap-2">
            <span className="text-lg">⚠️</span>
            <span className="font-semibold">You are offline. Scouting data may not be saved.</span>
          </div>
        </div>
      )}
	
      {/* Main Header */}
      <div className="
        w-full px-6 py-4
        bg-gradient-to-r from-white via-blue-50 to-indigo-50
        border-b border-gray-200 shadow-sm
        backdrop-blur-sm
      ">
        <div className="
          max-w-7xl mx-auto
          flex flex-col lg:flex-row lg:items-center lg:justify-between
          gap-4
        ">
          {/* Match Info */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="
                w-12 h-12 rounded-full
                bg-gradient-to-br from-blue-500 to-indigo-600
                flex items-center justify-center
                shadow-lg
              ">
                <span className="text-white text-xl">🏐</span>
              </div>
              <div>
                <h1 className={`${colors.typography.header.primary}`}>
                  {teamAName} vs {teamBName}
                </h1>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className={`
                    px-3 py-1 rounded-full
                    ${colors.primary.lightBg} ${colors.primary.text}
                    font-semibold
                  `}>
                    Set {currentSet}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span>Match ID: {matchId}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="
            flex items-center gap-3
            flex-wrap justify-center lg:justify-end
          ">
            {/* Reports */}
            <div className="flex gap-2">
              <button
                title="P2 Match Report"
                onClick={handleViewReportP2}
                className={`
                  px-4 py-2 rounded-lg
                  ${colors.buttons.secondary}
                  font-semibold
                  transition-all duration-200
                  focus:outline-none focus:ring-4 focus:ring-blue-300
                  hover:scale-105 shadow-lg
                `}
              >
                📋 P2
              </button>
              <button
                title="P3 Match Players Ranking"
                onClick={handleViewReportP3}
                className={`
                  px-4 py-2 rounded-lg
                  ${colors.buttons.secondary}
                  font-semibold
                  transition-all duration-200
                  focus:outline-none focus:ring-4 focus:ring-blue-300
                  hover:scale-105 shadow-lg
                `}
              >
                ⭐ P3
              </button>
            </div>

            {/* Divider */}
            <div className="w-px h-8 bg-gray-300 hidden lg:block" />

            {/* Match Controls */}
            <div className="flex gap-2">
              <button
                title="Manually End Set"
                onClick={() => setShowEndSetDialog(true)}
                className={`
                  px-4 py-2 rounded-lg
                  ${colors.buttons.tertiary}
                  font-semibold
                  transition-all duration-200
                  focus:outline-none focus:ring-4 focus:ring-blue-300
                  hover:scale-105 shadow-lg
                `}
              >
                🏁 End Set
              </button>
            </div>

            {/* Timer */}
            <div className="
              px-4 py-2 rounded-lg
              bg-white/80 backdrop-blur-sm
              border border-gray-200 shadow-sm
            ">
              <MatchTimer matchId={matchId} socket={socket} />
            </div>
          </div>
        </div>
      </div>

      {/* End Set Confirmation Dialog */}
      {showEndSetDialog && (
        <ConfirmationDialog
          title="End Set Manually?"
          message="Are you sure you want to end the current set? This action cannot be undone."
          onConfirm={handleEndSetConfirm}
          onCancel={() => setShowEndSetDialog(false)}
          confirmText="Yes, End Set"
          cancelText="Cancel"
        />
      )}
    </>
  );
}

export default MatchHeader;
